import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { SyutSelect } from 'components/ui/SyutSelect';
import notificationService from 'services/notificationService';
import { ledgerMappingService } from 'api/services/ledgerMappingService';
import { propertyService } from 'api/services/propertyService';
import type { FbLedger, LedgerMap, LedgerMapLeg, MappingLookups, UpsertLedgerMapRequest } from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect';

interface TransactionMapModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  record?: LedgerMap | null;
  lookups?: MappingLookups | null;
  onSave: () => void;
  onClose: () => void;
}

interface LegFormRow {
  entryType: string;
  mapType: string;
  mapKey: string;
  ledgerId: string;
}

interface TransactionMapFormData {
  propertyId: string;
  transactionType: string;
  accountingType: string;
  description: string;
  legs: LegFormRow[];
}

const DEFAULT_LEGS: LegFormRow[] = [
  { entryType: 'DEBIT', mapType: 'BANK_ACCOUNT', mapKey: '', ledgerId: '' },
  { entryType: 'CREDIT', mapType: 'ACCOUNTING_ROLE', mapKey: 'GUEST_ADVANCE', ledgerId: '' },
];

const INITIAL_FORM_DATA: TransactionMapFormData = {
  propertyId: '',
  transactionType: 'ADVANCE_RECEIPT',
  accountingType: '',
  description: '',
  legs: DEFAULT_LEGS,
};

/**
 * Editor for one transaction mapping: the Fairbooks voucher type code plus the
 * DEBIT/CREDIT legs that shape each posting. Saving REPLACES the legs with the
 * set shown here (the backend soft-deletes the old set in the same transaction).
 */
const TransactionMapModal: React.FC<TransactionMapModalProps> = ({
  isOpen, mode, record, lookups, onSave, onClose,
}) => {
  const [formData, setFormData] = useState<TransactionMapFormData>(INITIAL_FORM_DATA);
  const initialSnapshotRef = useRef('');
  const [formError, setFormError] = useState('');
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fbLedgerCacheRef = useRef<Record<string, FbLedger>>({});

  useEffect(() => {
    if (!isOpen) return;
    const baseData: TransactionMapFormData = mode === 'edit' && record
      ? {
          propertyId: record.propertyId || '',
          transactionType: record.transactionType,
          accountingType: record.accountingType,
          description: record.description || '',
          legs: record.details
            .slice()
            .sort((a, b) => a.sequenceNo - b.sequenceNo)
            .map(d => {
              if (d.ledgerId && d.ledgerCode) {
                fbLedgerCacheRef.current[d.ledgerId] = {
                  id: d.ledgerId, ledgerCode: d.ledgerCode, ledgerName: d.ledgerName || '',
                  normalBalance: '', ledgerNature: '',
                };
              }
              return { entryType: d.entryType, mapType: d.mapType, mapKey: d.mapKey || '', ledgerId: d.ledgerId || '' };
            }),
        }
      : { ...INITIAL_FORM_DATA, legs: DEFAULT_LEGS.map(l => ({ ...l })) };
    setFormData(baseData);
    initialSnapshotRef.current = JSON.stringify(baseData);
    setFormError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const handleClose = useCallback(() => {
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose]);

  const loadPropertyOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: input || undefined, pageSize: input ? 50 : 500, includeInactive: false });
    return res.data.map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
  }, []);

  const lastLedgerErrorRef = useRef('');
  const loadFbLedgerOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await ledgerMappingService.getFbLedgers({
        propertyId: formData.propertyId || undefined,
        search: input || undefined,
      });
      lastLedgerErrorRef.current = '';
      const ledgers = res.data ?? [];
      ledgers.forEach(l => { fbLedgerCacheRef.current[l.id] = l; });
      return ledgers.map(l => ({
        value: l.id,
        label: `${l.ledgerCode} - ${l.ledgerName}${l.groupName ? ` (${l.groupName})` : ''}`,
      }));
    } catch (err: any) {
      // Entity-scope setup errors (property not wired, multiple entities) come
      // back as 400s with an actionable message — show it once, not per keystroke.
      const msg = err?.response?.data?.message || 'Could not load Fairbooks ledgers.';
      if (lastLedgerErrorRef.current !== msg) {
        lastLedgerErrorRef.current = msg;
        notificationService.toast.error(msg, 6000);
      }
      return [];
    }
  }, [formData.propertyId]);

  const entryTypeOptions: SelectOption[] = useMemo(
    () => (lookups?.entryTypes ?? ['DEBIT', 'CREDIT']).map(t => ({ value: t, label: t })),
    [lookups]
  );
  const mapTypeOptions: SelectOption[] = useMemo(() => [
    { value: 'BANK_ACCOUNT', label: 'Bank Account' },
    { value: 'PAYMENT_MODE', label: 'Payment Mode' },
    { value: 'ACCOUNTING_ROLE', label: 'Accounting Role' },
  ], []);
  const paymentModeOptions: SelectOption[] = useMemo(
    () => (lookups?.paymentModes ?? []).map(m => ({ value: m, label: m })),
    [lookups]
  );
  const roleOptions: SelectOption[] = useMemo(
    () => (lookups?.accountingRoles ?? ['GUEST_ADVANCE']).map(r => ({ value: r, label: r })),
    [lookups]
  );
  const transactionTypeOptions: SelectOption[] = useMemo(
    () => (lookups?.transactionTypes ?? ['ADVANCE_RECEIPT']).map(t => ({ value: t, label: t })),
    [lookups]
  );

  const updateLeg = useCallback((index: number, patch: Partial<LegFormRow>) => {
    setFormData(prev => ({
      ...prev,
      legs: prev.legs.map((leg, i) => (i === index ? { ...leg, ...patch } : leg)),
    }));
  }, []);

  const addLeg = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      legs: [...prev.legs, { entryType: 'DEBIT', mapType: 'PAYMENT_MODE', mapKey: '', ledgerId: '' }],
    }));
  }, []);

  const removeLeg = useCallback((index: number) => {
    setFormData(prev => ({ ...prev, legs: prev.legs.filter((_, i) => i !== index) }));
  }, []);

  const handleSave = useCallback(() => {
    if (!formData.transactionType) { setFormError('Transaction Type is required.'); return; }
    if (!formData.accountingType.trim()) { setFormError('Voucher Type Code is required (the Fairbooks voucher type, e.g. CRSADV).'); return; }
    if (formData.legs.length === 0) { setFormError('At least one leg is required.'); return; }

    const hasDebit = formData.legs.some(l => l.entryType === 'DEBIT');
    const hasCredit = formData.legs.some(l => l.entryType === 'CREDIT');
    if (!hasDebit || !hasCredit) {
      setFormError('A mapping needs at least one DEBIT and one CREDIT leg — a one-sided mapping parks every posting.');
      return;
    }
    for (const leg of formData.legs) {
      if (leg.mapType === 'PAYMENT_MODE' && !leg.mapKey) { setFormError('Every Payment Mode leg needs a payment mode.'); return; }
      if (leg.mapType === 'ACCOUNTING_ROLE' && !leg.mapKey) { setFormError('Every Accounting Role leg needs a role.'); return; }
      if (leg.mapType === 'PAYMENT_MODE' && !leg.ledgerId) { setFormError(`The Payment Mode leg "${leg.mapKey}" needs a Fairbooks ledger — cash has no bank account to resolve through.`); return; }
    }
    setFormError('');
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const details: LedgerMapLeg[] = formData.legs.map((leg, i) => {
        const ledger = leg.ledgerId ? fbLedgerCacheRef.current[leg.ledgerId] : undefined;
        return {
          entryType: leg.entryType,
          sequenceNo: i + 1,
          mapType: leg.mapType,
          mapKey: leg.mapKey,
          ledgerId: leg.mapType === 'PAYMENT_MODE' ? leg.ledgerId : undefined,
          ledgerCode: leg.mapType === 'PAYMENT_MODE' ? ledger?.ledgerCode : undefined,
          ledgerName: leg.mapType === 'PAYMENT_MODE' ? ledger?.ledgerName : undefined,
        };
      });
      const payload: UpsertLedgerMapRequest = {
        propertyId: formData.propertyId || undefined,
        transactionType: formData.transactionType,
        accountingType: formData.accountingType.trim().toUpperCase(),
        description: formData.description.trim() || undefined,
        details,
      };
      await ledgerMappingService.upsertTransactionMap(payload);
      setIsSaving(false);
      notificationService.toast.success('Transaction mapping saved successfully.');
      onSave();
    } catch (err: any) {
      setIsSaving(false);
      const backendMessage = err?.response?.data?.message || err?.response?.data?.errors?.[0] || err?.message;
      notificationService.toast.error(backendMessage || 'An error occurred. Please try again.');
    }
  }, [formData, onSave]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        {isSaving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem' }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header">
          <h2 className="modal-title">{mode === 'create' ? 'Add Transaction Mapping' : 'Edit Transaction Mapping'}</h2>
          <button className="modal-close" onClick={handleClose} disabled={isSaving}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div className="form-grid-2">
            <div className="form-group" data-field="transactionType">
              <SyutSelect
                label="Transaction Type"
                options={transactionTypeOptions}
                value={formData.transactionType}
                onChange={(val) => setFormData(prev => ({ ...prev, transactionType: (val as string) || 'ADVANCE_RECEIPT' }))}
                isClearable={false}
                isDisabled={mode === 'edit'}
                size="md"
              />
            </div>
            <div className="form-group" data-field="propertyId">
              <SyutSelect
                label="Property Scope"
                isAsync
                loadOptions={loadPropertyOptions}
                defaultOptions={true}
                cacheOptions={false}
                value={formData.propertyId}
                onChange={(val) => setFormData(prev => ({ ...prev, propertyId: (val as string) || '' }))}
                placeholder="System default (all properties)"
                isClearable
                isDisabled={mode === 'edit'}
                size="md"
                menuPortalTarget={document.body}
              />
            </div>
          </div>

          <div className="form-grid-2" style={{ marginTop: '0.5rem' }}>
            <div className="form-group" data-field="accountingType">
              <label className="form-label">Voucher Type Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-input"
                value={formData.accountingType}
                onChange={(e) => setFormData(prev => ({ ...prev, accountingType: e.target.value.toUpperCase() }))}
                placeholder="e.g., CRSADV"
                maxLength={20}
              />
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                The Fairbooks voucher type this posting creates. It must exist in Fairbooks with manual numbering.
              </div>
            </div>
            <div className="form-group" data-field="description">
              <label className="form-label">Description</label>
              <input
                type="text"
                className="form-input"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                maxLength={200}
              />
            </div>
          </div>

          <div style={{ marginTop: '0.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Legs</h3>
              <button className="btn btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.75rem' }} onClick={addLeg}>
                <IconSystem.add size={14} /> Add Leg
              </button>
            </div>
            <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
              Bank Account legs need no ledger here — the actual bank resolves through the Ledger Mappings tab.
              Only Payment Mode legs (e.g. Cash) carry a ledger directly.
            </p>

            {formData.legs.map((leg, index) => (
              <div
                key={index}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '110px 150px 1fr 1fr 32px',
                  gap: '0.5rem',
                  alignItems: 'end',
                  padding: '0.5rem',
                  marginBottom: '0.5rem',
                  border: '1px solid var(--border-color)',
                  borderRadius: '0.375rem',
                  background: 'var(--bg-secondary)',
                }}
              >
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <SyutSelect
                    label="Entry"
                    options={entryTypeOptions}
                    value={leg.entryType}
                    onChange={(val) => updateLeg(index, { entryType: (val as string) || 'DEBIT' })}
                    isClearable={false}
                    size="sm"
                    menuPortalTarget={document.body}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <SyutSelect
                    label="Resolves By"
                    options={mapTypeOptions}
                    value={leg.mapType}
                    onChange={(val) => updateLeg(index, { mapType: (val as string) || 'BANK_ACCOUNT', mapKey: '', ledgerId: '' })}
                    isClearable={false}
                    size="sm"
                    menuPortalTarget={document.body}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  {leg.mapType === 'BANK_ACCOUNT' ? (
                    <>
                      <label className="form-label">Source</label>
                      <input type="text" className="form-input" value="Any bank account (via Ledger Mappings)" disabled style={{ fontSize: '0.78rem' }} />
                    </>
                  ) : leg.mapType === 'PAYMENT_MODE' ? (
                    <SyutSelect
                      label="Payment Mode"
                      options={paymentModeOptions}
                      value={leg.mapKey}
                      onChange={(val) => updateLeg(index, { mapKey: (val as string) || '' })}
                      placeholder="Select mode..."
                      isClearable={false}
                      size="sm"
                      menuPortalTarget={document.body}
                    />
                  ) : (
                    <SyutSelect
                      label="Role"
                      options={roleOptions}
                      value={leg.mapKey}
                      onChange={(val) => updateLeg(index, { mapKey: (val as string) || '' })}
                      placeholder="Select role..."
                      isClearable={false}
                      size="sm"
                      menuPortalTarget={document.body}
                    />
                  )}
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  {leg.mapType === 'PAYMENT_MODE' ? (
                    <SyutSelect
                      label="Fairbooks Ledger"
                      isAsync
                      loadOptions={loadFbLedgerOptions}
                      defaultOptions={true}
                      cacheOptions={false}
                      value={leg.ledgerId}
                      onChange={(val) => updateLeg(index, { ledgerId: (val as string) || '' })}
                      placeholder="Search ledgers..."
                      isClearable
                      size="sm"
                      menuPortalTarget={document.body}
                    />
                  ) : (
                    <>
                      <label className="form-label">Fairbooks Ledger</label>
                      <input type="text" className="form-input" value="Resolved via Ledger Mappings" disabled style={{ fontSize: '0.78rem' }} />
                    </>
                  )}
                </div>
                <button
                  onClick={() => removeLeg(index)}
                  title="Remove leg"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.4rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}
                >
                  <IconSystem.trash size={16} />
                </button>
              </div>
            ))}
          </div>

          {formError && (
            <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', borderRadius: '0.375rem', background: 'var(--danger-bg, rgba(220,38,38,0.08))', color: 'var(--danger, #dc2626)', fontSize: '0.8rem' }}>
              {formError}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving || (mode === 'edit' && !isDirty)}
            title={mode === 'edit' && !isDirty ? 'No changes to save' : undefined}
          >
            <IconSystem.save size={16} />
            Save Mapping
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Save"
        message="Saving replaces this mapping's legs with the set shown here, and new postings use it immediately. Continue?"
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default TransactionMapModal;
